/**
 * Layout of the Gesellschaften org chart: owners above what they hold, one row per level.
 *
 * Shareholdings form a layered graph rather than a tree (a Gesellschaft can have several owners,
 * e.g. 50/50), so this is a small Sugiyama-style layout: levels by longest path from the top,
 * order within a row by the owners' average position, then x positions without overlap.
 *
 * Boxes with a band (the Verkaufsmodell) get their own row per band inside their level, drawn
 * as a labelled area like on the whiteboard; lines into a lower row run down a trunk on the
 * left so they don't cross the boxes in between. Each owner and kind of line gets its own lane
 * and its own horizontal track per gap, so no two lines run on top of each other.
 */

export interface OrgChartNodeInput {
  key: string;
  /** Band (Verkaufsmodell) the box is grouped in; none: the level's plain row. */
  band?: string | null;
  /** Box width, so a long name fits; default NODE_WIDTH. */
  width?: number;
}

export interface OrgChartEdgeInput {
  from: string;
  to: string;
  /** Kind of line (e.g. the shareholder role): lines of one owner and kind may share a stretch. */
  style?: string | null;
}

export interface PlacedNode {
  key: string;
  level: number;
  x: number;
  y: number;
  width: number;
}

export interface PlacedEdge {
  from: string;
  to: string;
  /** Orthogonal line from the bottom of the owner to the top of the held box. */
  points: { x: number; y: number }[];
  /** Where the share label sits: on the last segment, below every horizontal line into the row. */
  labelY: number;
}

export interface PlacedBand {
  band: string;
  level: number;
  /** Boxes in the band, so it can follow them when they are moved by hand. */
  keys: string[];
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface OrgChartLayout {
  nodes: PlacedNode[];
  edges: PlacedEdge[];
  bands: PlacedBand[];
  width: number;
  height: number;
}

export const NODE_WIDTH = 220;
/** Room for the name, the Geschäftsführung and the Buchhaltung line. */
export const NODE_HEIGHT = 80;
export const H_GAP = 32;
/** Room between rows for the lines and their labels (two labels stacked while a box is selected). */
export const V_GAP = 136;
/** Room for the band's label above its boxes. */
export const BAND_LABEL_HEIGHT = 28;
const BAND_PADDING = 16;
/** A moved box's band joins the rest of its band again within this distance. */
const BAND_MERGE_GAP = 16;
/** The trunk runs this far left of the leftmost box, outside every band. */
const TRUNK_OFFSET = 40;
/** Each owner and kind of line down the trunk gets its own lane, so solid and dashed lines don't merge. */
const TRUNK_LANE = 14;
/** Distance between two tracks in a gap. */
const TRACK_GAP = 12;
/** Height a gap offers for tracks above the lowest one, clear of the box above; more tracks make it taller. */
const TRACK_ROOM = 48;

interface Row {
  level: number;
  band: string | null;
  keys: string[];
  top: number;
}

/** One group's horizontal line in a gap. */
interface Track {
  top: Set<number>;
  bottom: Set<number>;
  min: number;
  max: number;
  y: number;
}

export function layoutOrgChart(
  nodes: OrgChartNodeInput[],
  edges: OrgChartEdgeInput[],
  bandOrder: string[] = []
): OrgChartLayout {
  const keys = new Set(nodes.map(n => n.key));
  // Lines to unknown boxes and self-lines can't be drawn; the backend prevents cycles.
  const links = edges.filter(e => keys.has(e.from) && keys.has(e.to) && e.from !== e.to);
  const bandOf = new Map(nodes.map(n => [n.key, n.band ?? null]));
  const widthOf = new Map(nodes.map(n => [n.key, Math.max(n.width ?? NODE_WIDTH, NODE_WIDTH)]));
  const w = (k: string) => widthOf.get(k)!;

  const parents = new Map<string, string[]>();
  const children = new Map<string, string[]>();
  for (const n of nodes) {
    parents.set(n.key, []);
    children.set(n.key, []);
  }
  for (const e of links) {
    parents.get(e.to)!.push(e.from);
    children.get(e.from)!.push(e.to);
  }

  // Level = longest path from a box without owners, so a box sits below all of its owners.
  const level = new Map<string, number>();
  const levelOf = (key: string, visiting: Set<string>): number => {
    const known = level.get(key);
    if (known !== undefined) return known;
    if (visiting.has(key)) return 0; // defensive: a cycle must not hang the page
    visiting.add(key);
    const own = Math.max(-1, ...parents.get(key)!.map(p => levelOf(p, visiting))) + 1;
    visiting.delete(key);
    level.set(key, own);
    return own;
  };
  nodes.forEach(n => levelOf(n.key, new Set()));
  const levelCount = nodes.length ? Math.max(...[...level.values()]) + 1 : 0;

  // Rows: per level the plain row first, then one row per band in the given order.
  const knownBands = [...bandOrder, ...[...new Set(nodes.map(n => n.band).filter((b): b is string => !!b))].filter(b => !bandOrder.includes(b))];
  const rows: Row[] = [];
  for (let l = 0; l < levelCount; l++) {
    for (const band of [null, ...knownBands]) {
      const rowKeys = nodes.filter(n => level.get(n.key) === l && (bandOf.get(n.key) ?? null) === band).map(n => n.key);
      if (rowKeys.length) rows.push({ level: l, band, keys: rowKeys, top: 0 });
    }
  }
  const rowOf = new Map<string, number>();
  rows.forEach((row, i) => row.keys.forEach(k => rowOf.set(k, i)));

  // Boxes without any line go last in their row, as their own group.
  const isolated = (key: string) => parents.get(key)!.length === 0 && children.get(key)!.length === 0;

  // x (left edge), row by row from the top: each box wants its centre under the centre of its
  // owners (already placed, they're in earlier rows); boxes are pushed right where they would overlap.
  const x = new Map<string, number>();
  const centre = (k: string) => x.get(k)! + w(k) / 2;
  for (const row of rows) {
    const wanted = (k: string) => {
      const ps = parents.get(k)!;
      return ps.length ? ps.reduce((sum, p) => sum + centre(p), 0) / ps.length - w(k) / 2 : null;
    };
    const original = new Map(row.keys.map((k, i) => [k, i]));
    row.keys = [...row.keys].sort((a, b) => {
      if (isolated(a) !== isolated(b)) return isolated(a) ? 1 : -1;
      const wa = wanted(a);
      const wb = wanted(b);
      if (wa !== null && wb !== null && wa !== wb) return wa - wb;
      return original.get(a)! - original.get(b)!;
    });
    let nextFree = 0;
    for (const k of row.keys) {
      const placed = Math.max(wanted(k) ?? nextFree, nextFree);
      x.set(k, placed);
      nextFree = placed + w(k) + H_GAP;
    }
  }

  // Owners: centre them over the boxes they hold in the next row, where their row leaves room.
  for (let r = rows.length - 2; r >= 0; r--) {
    const row = rows[r].keys;
    for (let i = 0; i < row.length; i++) {
      const cs = children.get(row[i])!.filter(c => rowOf.get(c) === r + 1);
      if (!cs.length) continue;
      const wantedLeft = cs.reduce((sum, c) => sum + centre(c), 0) / cs.length - w(row[i]) / 2;
      const min = i > 0 ? x.get(row[i - 1])! + w(row[i - 1]) + H_GAP : -Infinity;
      const max = i < row.length - 1 ? x.get(row[i + 1])! - w(row[i]) - H_GAP : Infinity;
      x.set(row[i], Math.min(Math.max(wantedLeft, min), max));
    }
  }

  const hasBands = rows.some(r => r.band);
  const minBoxX = Math.min(...[...x.values()]);
  const skips = (e: OrgChartEdgeInput) => rowOf.get(e.to)! > rowOf.get(e.from)! + 1;
  // Lines of one owner and one kind (e.g. all its Kommanditist lines) form one branching line;
  // lines of different owners or kinds never share a stretch.
  const groupOf = (e: OrgChartEdgeInput) => `${e.from}\u0000${e.style ?? ''}`;

  // Trunk: one lane per group whose lines skip rows. The group that joins latest and leaves
  // earliest runs innermost, so the other groups' lines have the fewest lanes to cross.
  const trunkSpan = new Map<string, { first: number; last: number; ownerX: number }>();
  for (const e of links.filter(skips)) {
    const g = groupOf(e);
    const span = trunkSpan.get(g) ?? { first: rowOf.get(e.from)! + 1, last: 0, ownerX: x.get(e.from)! };
    span.last = Math.max(span.last, rowOf.get(e.to)!);
    trunkSpan.set(g, span);
  }
  const lanes = [...trunkSpan.entries()].sort(([, a], [, b]) => a.last - b.last || b.first - a.first || b.ownerX - a.ownerX);
  const laneX = new Map(lanes.map(([g], i) => [g, minBoxX - TRUNK_OFFSET - i * TRUNK_LANE]));
  const trunkX = minBoxX - TRUNK_OFFSET - Math.max(0, lanes.length - 1) * TRUNK_LANE;

  // An owner with lines of several kinds sends each kind out at its own point, ordered by where
  // they head, so a solid and a dashed line never run down on top of each other.
  const exitX = new Map<string, number>();
  for (const n of nodes) {
    const out = links.filter(e => e.from === n.key);
    const target = (g: string) => {
      const own = out.filter(e => groupOf(e) === g);
      return own.reduce((sum, e) => sum + (skips(e) ? laneX.get(g)! : centre(e.to)), 0) / own.length;
    };
    const groups = [...new Set(out.map(groupOf))].sort((a, b) => target(a) - target(b));
    groups.forEach((g, i) => exitX.set(g, x.get(n.key)! + (w(n.key) * (i + 0.5)) / groups.length));
  }
  const sourceX = (e: OrgChartEdgeInput) => (skips(e) ? laneX.get(groupOf(e))! : exitX.get(groupOf(e))!);

  // Several lines into one box (e.g. Komplementär and Kommanditist) enter side by side, ordered
  // like where they come from (the trunk on the far left), so neither lines nor labels collide.
  const entryX = new Map<OrgChartEdgeInput, number>();
  for (const n of nodes) {
    const incoming = links.filter(e => e.to === n.key).sort((a, b) => sourceX(a) - sourceX(b));
    incoming.forEach((e, i) => entryX.set(e, x.get(n.key)! + (w(n.key) * (i + 0.5)) / incoming.length));
  }

  // Tracks: every group gets its own horizontal line in each gap it passes (gap g lies above row g).
  // Pins are the verticals that end at the track from above or leave it downwards.
  const tracksByGap = new Map<number, Map<string, Track>>();
  const trackFor = (gap: number, g: string) => {
    const inGap = tracksByGap.get(gap) ?? new Map<string, Track>();
    tracksByGap.set(gap, inGap);
    const track = inGap.get(g) ?? { top: new Set<number>(), bottom: new Set<number>(), min: Infinity, max: -Infinity, y: 0 };
    inGap.set(g, track);
    return track;
  };
  const pin = (track: Track, side: 'top' | 'bottom', px: number) => {
    track[side].add(px);
    track.min = Math.min(track.min, px);
    track.max = Math.max(track.max, px);
  };
  for (const e of links) {
    const g = groupOf(e);
    const childRow = rowOf.get(e.to)!;
    if (childRow <= rowOf.get(e.from)!) continue; // only in a cycle; drawn without a track
    const out = trackFor(rowOf.get(e.from)! + 1, g);
    pin(out, 'top', exitX.get(g)!);
    if (!skips(e)) {
      pin(out, 'bottom', entryX.get(e)!);
      continue;
    }
    pin(out, 'bottom', laneX.get(g)!);
    const into = trackFor(childRow, g);
    pin(into, 'top', laneX.get(g)!);
    pin(into, 'bottom', entryX.get(e)!);
  }
  // A lane running on below its track passes the whole gap: not a pin, the same in any order.
  for (const [g, span] of trunkSpan) {
    for (let gap = span.first + 1; gap < span.last; gap++) tracksByGap.get(gap)?.get(g)?.top.delete(laneX.get(g)!);
  }

  // y: rows stacked, a band row with room for its label, a gap with many tracks taller.
  const orderedTracks = new Map([...tracksByGap].map(([gap, tracks]) => [gap, orderTracks([...tracks.values()])]));
  let top = 0;
  rows.forEach((row, r) => {
    const count = orderedTracks.get(r)?.length ?? 0;
    top += Math.max(0, count - 1 - Math.floor(TRACK_ROOM / TRACK_GAP)) * TRACK_GAP;
    row.top = top;
    top += (row.band ? BAND_LABEL_HEIGHT : 0) + NODE_HEIGHT + V_GAP;
  });
  for (const [gap, ordered] of orderedTracks) {
    // The lowest track sits where a single line turns; the others stack up above it.
    const lowest = rows[gap].top - V_GAP / 2;
    ordered.forEach((track, i) => (track.y = lowest - (ordered.length - 1 - i) * TRACK_GAP));
  }
  const boxY = (k: string) => {
    const row = rows[rowOf.get(k)!];
    return row.top + (row.band ? BAND_LABEL_HEIGHT : 0);
  };

  // Everything moves right so the outermost lane (or the leftmost box) starts at 0.
  const shift = -(rows.length ? (lanes.length || hasBands ? trunkX - 8 : minBoxX) : 0);

  const placed: PlacedNode[] = nodes.map(n => ({
    key: n.key,
    level: level.get(n.key)!,
    x: x.get(n.key)! + shift,
    y: boxY(n.key),
    width: w(n.key),
  }));

  const bands: PlacedBand[] = rows
    .filter(r => r.band)
    .map(r => {
      const xs = r.keys.map(k => x.get(k)! + shift);
      const rights = r.keys.map(k => x.get(k)! + shift + w(k));
      const left = Math.min(...xs) - BAND_PADDING;
      return {
        band: r.band!,
        level: r.level,
        keys: r.keys,
        x: left,
        y: r.top - 8,
        width: Math.max(...rights) + BAND_PADDING - left,
        height: BAND_LABEL_HEIGHT + NODE_HEIGHT + 8 + BAND_PADDING,
      };
    });

  const placedEdges: PlacedEdge[] = links.map(e => {
    const g = groupOf(e);
    const startX = exitX.get(g)! + shift;
    const startY = boxY(e.from) + NODE_HEIGHT;
    const endX = entryX.get(e)! + shift;
    const endY = boxY(e.to);
    const outY = tracksByGap.get(rowOf.get(e.from)! + 1)?.get(g)?.y ?? rows[rowOf.get(e.to)!].top - V_GAP / 2;
    // The share label sits on the last stretch, below every track of the gap.
    const labelY = rows[rowOf.get(e.to)!].top - V_GAP / 2 + 30;
    if (!skips(e)) {
      return {
        from: e.from,
        to: e.to,
        points: [{ x: startX, y: startY }, { x: startX, y: outY }, { x: endX, y: outY }, { x: endX, y: endY }],
        labelY,
      };
    }
    // Further down: out to the group's lane on the left, down, and back in above the held box's row.
    const trunk = laneX.get(g)! + shift;
    const inY = tracksByGap.get(rowOf.get(e.to)!)!.get(g)!.y;
    return {
      from: e.from,
      to: e.to,
      points: [
        { x: startX, y: startY },
        { x: startX, y: outY },
        { x: trunk, y: outY },
        { x: trunk, y: inY },
        { x: endX, y: inY },
        { x: endX, y: endY },
      ],
      labelY,
    };
  });

  const right = Math.max(0, ...placed.map(p => p.x + p.width), ...bands.map(b => b.x + b.width));
  const bottom = Math.max(0, ...placed.map(p => p.y + NODE_HEIGHT), ...bands.map(b => b.y + b.height));
  return { nodes: placed, edges: placedEdges, bands, width: right, height: bottom };
}

/**
 * Top-to-bottom order of a gap's tracks with the fewest crossings: a pin crosses another track
 * when its vertical passes that track's horizontal stretch. Exact for the usual handful of
 * tracks, swaps of neighbours beyond that.
 */
function orderTracks(tracks: Track[]): Track[] {
  const inside = (px: number, t: Track) => px > t.min + 0.5 && px < t.max - 0.5;
  const count = (pins: Set<number>, t: Track) => [...pins].filter(px => inside(px, t)).length;
  const above = (a: Track, b: Track) => count(a.bottom, b) + count(b.top, a);
  const n = tracks.length;
  if (n > 12) {
    const order = [...tracks];
    for (let swapped = true; swapped; ) {
      swapped = false;
      for (let i = 0; i + 1 < n; i++) {
        if (above(order[i + 1], order[i]) < above(order[i], order[i + 1])) {
          [order[i], order[i + 1]] = [order[i + 1], order[i]];
          swapped = true;
        }
      }
    }
    return order;
  }
  // best[set] = fewest crossings with the tracks of `set` on top; the next one goes below them.
  const best: { cost: number; last: number; prev: number }[] = [{ cost: 0, last: -1, prev: -1 }];
  for (let set = 1; set < 1 << n; set++) {
    best[set] = { cost: Infinity, last: -1, prev: -1 };
    for (let t = 0; t < n; t++) {
      if (!(set & (1 << t))) continue;
      const rest = set & ~(1 << t);
      let cost = best[rest].cost;
      for (let a = 0; a < n; a++) if (rest & (1 << a)) cost += above(tracks[a], tracks[t]);
      if (cost < best[set].cost) best[set] = { cost, last: t, prev: rest };
    }
  }
  const order: Track[] = [];
  for (let set = (1 << n) - 1; set; set = best[set].prev) order.unshift(tracks[best[set].last]);
  return order;
}

/** Where a box was moved by hand (its top left corner, in chart coordinates). */
export type BoxPositions = Record<string, { x: number; y: number }>;

/**
 * The layout with some boxes moved by hand: those boxes at their own place, their lines drawn
 * anew between them (out at the same point of the owner, in at the same point of the held box).
 * A moved box takes its band along as a band of its own; the rest of the band closes around the
 * boxes that stayed. Lines between boxes that stayed keep the computed routing.
 */
export function applyBoxPositions(layout: OrgChartLayout, positions: BoxPositions): OrgChartLayout {
  const moved = new Set(layout.nodes.filter(n => positions[n.key]).map(n => n.key));
  if (!moved.size) return layout;

  const original = new Map(layout.nodes.map(n => [n.key, n]));
  const nodes = layout.nodes.map(n => (moved.has(n.key) ? { ...n, x: positions[n.key].x, y: positions[n.key].y } : n));
  const byKey = new Map(nodes.map(n => [n.key, n]));

  const edges = layout.edges.map(e => {
    if (!moved.has(e.from) && !moved.has(e.to)) return e;
    const from = byKey.get(e.from)!;
    const to = byKey.get(e.to)!;
    const first = e.points[0];
    const last = e.points[e.points.length - 1];
    const sx = from.x + (first.x - original.get(e.from)!.x);
    const sy = from.y + NODE_HEIGHT;
    const ex = to.x + (last.x - original.get(e.to)!.x);
    const ey = to.y;
    if (ey - sy >= 32) {
      // Held box below: down, across halfway, down into it.
      const midY = sy + (ey - sy) / 2;
      return {
        ...e,
        points: [{ x: sx, y: sy }, { x: sx, y: midY }, { x: ex, y: midY }, { x: ex, y: ey }],
        labelY: Math.max(midY + 10, ey - 38),
      };
    }
    // Held box beside or above: out below the owner, across between them, in from above.
    const midX = (sx + ex) / 2;
    return {
      ...e,
      points: [
        { x: sx, y: sy },
        { x: sx, y: sy + 16 },
        { x: midX, y: sy + 16 },
        { x: midX, y: ey - 16 },
        { x: ex, y: ey - 16 },
        { x: ex, y: ey },
      ],
      labelY: ey - 30,
    };
  });

  const around = (b: PlacedBand, keys: string[]): PlacedBand => {
    const members = keys.map(k => byKey.get(k)!);
    const left = Math.min(...members.map(m => m.x)) - BAND_PADDING;
    const top = Math.min(...members.map(m => m.y)) - BAND_LABEL_HEIGHT - 8;
    const right = Math.max(...members.map(m => m.x + m.width)) + BAND_PADDING;
    const bottom = Math.max(...members.map(m => m.y)) + NODE_HEIGHT + BAND_PADDING;
    return { ...b, keys, x: left, y: top, width: right - left, height: bottom - top };
  };
  // The boxes that stayed form one part of the band, each moved box its own; parts that touch
  // or overlap merge again (a box dragged back joins its band), until none touch any more.
  const touch = (a: PlacedBand, c: PlacedBand) =>
    a.x <= c.x + c.width + BAND_MERGE_GAP &&
    c.x <= a.x + a.width + BAND_MERGE_GAP &&
    a.y <= c.y + c.height + BAND_MERGE_GAP &&
    c.y <= a.y + a.height + BAND_MERGE_GAP;
  const bands = layout.bands.flatMap(b => {
    if (!b.keys.some(k => moved.has(k))) return [b];
    const stayed = b.keys.filter(k => !moved.has(k));
    let parts = [...(stayed.length ? [around(b, stayed)] : []), ...b.keys.filter(k => moved.has(k)).map(k => around(b, [k]))];
    for (let merged = true; merged; ) {
      merged = false;
      for (let i = 0; i < parts.length && !merged; i++) {
        for (let j = i + 1; j < parts.length && !merged; j++) {
          if (touch(parts[i], parts[j])) {
            const joined = around(b, [...parts[i].keys, ...parts[j].keys]);
            parts = [...parts.filter((_, k) => k !== i && k !== j), joined];
            merged = true;
          }
        }
      }
    }
    return parts;
  });

  const width = Math.max(0, ...nodes.map(n => n.x + n.width), ...bands.map(b => b.x + b.width), ...edges.flatMap(e => e.points.map(p => p.x)));
  const height = Math.max(0, ...nodes.map(n => n.y + NODE_HEIGHT), ...bands.map(b => b.y + b.height));
  return { nodes, edges, bands, width, height };
}

// ---- lines adjusted by hand (like elbow connectors in Miro) ----

export interface Point {
  x: number;
  y: number;
}

/** A line reshaped by hand: its corner points, as `toElbow` gives them. */
export type LineOverrides = Record<string, { points: Point[] }>;

/** Fixed bit of line at the owner and at the held box, so a line always meets a box straight. */
const ELBOW_STUB = 16;

export const lineId = (e: { from: string; to: string }) => `${e.from}>${e.to}`;

/**
 * The line as an elbow connector that can be reshaped: segments alternate vertical (even index)
 * and horizontal (odd index), zero-length ones included, with a fixed vertical stub at both ends.
 * So every segment between the stubs can be moved across its direction and the neighbours,
 * always perpendicular to it, just stretch.
 */
export function toElbow(points: Point[]): Point[] {
  const start = points[0];
  const end = points[points.length - 1];
  const padded = [start, { x: start.x, y: start.y + ELBOW_STUB }, ...points.slice(1, -1), { x: end.x, y: end.y - ELBOW_STUB }, end];
  const out: Point[] = [{ ...start }];
  let vertical = true;
  for (const next of padded.slice(1)) {
    const last = out[out.length - 1];
    if (!(vertical ? next.x === last.x : next.y === last.y)) {
      out.push({ ...last }); // zero-length segment of the expected direction
      vertical = !vertical;
    }
    out.push({ ...next });
    vertical = !vertical;
  }
  return out;
}

/** Segments of an elbow line that can be moved: all but the stubs, and none of zero length. */
export function movableSegments(points: Point[]): number[] {
  const result: number[] = [];
  for (let i = 1; i < points.length - 2; i++) {
    if (Math.abs(points[i].x - points[i + 1].x) + Math.abs(points[i].y - points[i + 1].y) >= 1) result.push(i);
  }
  return result;
}

/** Moves segment `index` of an elbow line across its direction: a vertical one to x = value, a horizontal one to y = value. */
export function moveSegment(points: Point[], index: number, value: number): Point[] {
  const vertical = index % 2 === 0;
  return points.map((p, i) => (i === index || i === index + 1 ? (vertical ? { x: value, y: p.y } : { x: p.x, y: value }) : p));
}

/**
 * The layout with the lines reshaped by hand. A reshaped line only counts while it still starts
 * and ends where its boxes are (moving a box puts its lines back to the automatic routing).
 * The share label goes on the last vertical run into the held box.
 */
export function applyLineOverrides(layout: OrgChartLayout, overrides: LineOverrides): OrgChartLayout {
  const same = (a: Point, b: Point) => Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5;
  let changed = false;
  const edges = layout.edges.map(e => {
    const points = overrides[lineId(e)]?.points;
    if (!points || points.length < 2) return e;
    if (!same(points[0], e.points[0]) || !same(points[points.length - 1], e.points[e.points.length - 1])) return e;
    changed = true;
    const end = points[points.length - 1];
    // Up the last vertical run from the box to where the line turns into it.
    let turn = points.length - 1;
    while (turn > 0 && Math.abs(points[turn - 1].x - end.x) < 0.5) turn--;
    const turnY = points[turn].y;
    const labelY = turnY < end.y ? Math.max(turnY + 12, end.y - 38) : end.y - 12;
    return { ...e, points, labelY };
  });
  return changed ? { ...layout, edges } : layout;
}

/** A rectangle in chart coordinates. */
export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Room around the boxes in the USt area: their bands fit inside, its label above them. */
const VAT_AREA_SIDE = 28;
const VAT_AREA_TOP = 64;

type FrameBox = { x: number; y: number; width: number };

/** Frame around some boxes: their bounding box plus room for their bands and the label. */
function frameAround(boxes: FrameBox[]): Area {
  const left = Math.min(...boxes.map(b => b.x)) - VAT_AREA_SIDE;
  const top = Math.min(...boxes.map(b => b.y)) - VAT_AREA_TOP;
  const right = Math.max(...boxes.map(b => b.x + b.width)) + VAT_AREA_SIDE;
  const bottom = Math.max(...boxes.map(b => b.y)) + NODE_HEIGHT + VAT_AREA_SIDE;
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * The "USt." frames of the whiteboard, but never around a box that isn't umsatzsteuerpflichtig:
 * every such box starts with a frame of its own; the two whose joint frame is smallest are merged
 * as long as that frame holds none of the `others`, until no pair can be merged any more.
 */
export function vatAreas(vatBoxes: FrameBox[], others: FrameBox[]): Area[] {
  const hits = (area: Area, b: FrameBox) =>
    b.x < area.x + area.width && b.x + b.width > area.x && b.y < area.y + area.height && b.y + NODE_HEIGHT > area.y;
  let groups = vatBoxes.map(b => [b]);
  for (;;) {
    let best: { i: number; j: number; size: number } | null = null;
    for (let i = 0; i < groups.length; i++) {
      for (let j = i + 1; j < groups.length; j++) {
        const area = frameAround([...groups[i], ...groups[j]]);
        if (others.some(o => hits(area, o))) continue;
        const size = area.width * area.height;
        if (!best || size < best.size) best = { i, j, size };
      }
    }
    if (!best) break;
    const merged = [...groups[best.i], ...groups[best.j]];
    groups = [...groups.filter((_, k) => k !== best!.i && k !== best!.j), merged];
  }
  return groups.map(frameAround);
}
